import React, { useContext } from "react";
import { groupBy, toUpper } from "lodash";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useModal } from "react-modal-hook";
import { SUBMISSION_CATEGORY } from "common/constants";
import { getSubmissionCategoryTranslationKey } from "common/utils/submissions";
import { CurrentUserQuery, SubmissionType } from "../../../generated/graphql";
import { AuthContext } from "../../Authorization/AuthContext";
import { SelectionCard } from "../../Inputs/SelectionCard";
import { SelectionCardOption } from "../../Inputs/SelectionCard";
import { canCreateSubmissionTypeFromAuthInfo } from "../../Submissions/utils";
import {
  ContentSection,
  HeaderSection,
  UpdatedContainer as Container,
} from "../__styles__/Modal";
import { Button } from "../Button";
import { iconNames } from "../Icons/LucideIcons";
import Modal from "../Modal";
import { Body, Title } from "../Typography";

export const CreateSubmissionButton = ({
  onSubmit,
  disabled,
  submissionTypeFilter,
  buttonText = "Add new",
  hideIfNoOptions = false,
  ...props
}: Omit<SelectSubmissionTypeProps, "onCancel" | "submissionTypes"> & {
  disabled: boolean;
  submissionTypeFilter: (
    submissionType: NonNullable<
      CurrentUserQuery["currentUser"]
    >["account"]["submissionTypes"][number],
  ) => boolean;
}) => {
  const { account, user, admin, publicUser } = useContext(AuthContext);
  const submissionTypes = (
    account?.submissionTypes.filter(submissionTypeFilter) ?? []
  ).filter(submissionType =>
    canCreateSubmissionTypeFromAuthInfo({
      submissionType,
      user,
      admin,
      publicUser,
    }),
  );

  const [showSelectInspectionModal] = useSelectSubmissionTypeModal({
    ...props,
    submissionTypes,
    onSubmit,
  });

  if (hideIfNoOptions && submissionTypes.length === 0) {
    return null;
  }

  return (
    <Button
      onClick={
        submissionTypes.length === 1 ?
          () => onSubmit(submissionTypes[0]!.id)
        : showSelectInspectionModal
      }
      size="small"
      disabled={disabled || submissionTypes.length === 0}
      styleVariant="primary"
      leftIconName="plus"
    >
      {buttonText}
    </Button>
  );
};

export type SelectSubmissionTypeProps = {
  objectDisplay?: string;
  onCancel: () => void;
  onSubmit: (submissionTypeId: string) => void;
  submissionTypes: Array<Pick<SubmissionType, "id" | "name" | "category">>;
  buttonText?: string;
  hideIfNoOptions?: boolean;
  isPublic?: boolean;
};

const useSelectSubmissionTypeModal = (
  props: Omit<SelectSubmissionTypeProps, "onCancel">,
) => {
  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={hideModal}>
        <SelectSubmissionType onCancel={hideModal} {...props} />
      </Modal>
    ),
    [props.submissionTypes],
  );

  return [showModal, hideModal];
};

type SelectSubmissionTypeFormStructure = {
  submissionTypeId: string;
};

const SelectSubmissionType = ({
  onSubmit,
  objectDisplay,
  submissionTypes,
  isPublic,
}: SelectSubmissionTypeProps) => {
  const { t } = useTranslation();
  const submissionTypesByCategory = groupBy(submissionTypes, f => f.category);

  const { control, handleSubmit } =
    useForm<SelectSubmissionTypeFormStructure>();

  const groupedOptions = {} as Record<string, Array<SelectionCardOption>>;
  Object.entries(submissionTypesByCategory).forEach(
    ([category, submissionTypes]) => {
      let translatedCategory = toUpper(
        t(
          getSubmissionCategoryTranslationKey({ plural: true })[
            category as SUBMISSION_CATEGORY
          ],
        ),
      );

      const typeOptions = submissionTypes.map(submissionType => {
        return {
          id: submissionType.id,
          value: submissionType.id,
          title: submissionType.name,
          iconName: iconNames.find(icon => icon === "clipboard-check"),
        };
      });
      groupedOptions[translatedCategory] = typeOptions;
    },
  );

  return (
    <Container size="medium">
      <HeaderSection>
        <Title size="small" type="semiBold">
          {isPublic ? t("property-button-new-record") : "New record"}
        </Title>
        <Body size="default" type="regular" color="contentSecondary">
          {objectDisplay}
        </Body>
      </HeaderSection>
      <ContentSection>
        <Controller
          name="submissionTypeId"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange } }) => (
            <SelectionCard.Group
              handleSelection={(value: string) => {
                onChange(value);
                void handleSubmit(() => {
                  onSubmit(value);
                })();
              }}
              direction="vertical"
              spacing="compact"
            >
              {Object.entries(groupedOptions).map(
                ([category, options], index) => (
                  <React.Fragment key={index}>
                    <Body size="small" type="emphasis">
                      {category}
                    </Body>
                    {options.map(option => (
                      <SelectionCard.Item
                        key={option.id}
                        value={option.id}
                        borderStyle="ghost"
                        size="compact"
                      >
                        <SelectionCard.CommonContent
                          iconName={option.iconName}
                          title={option.title}
                        />
                      </SelectionCard.Item>
                    ))}
                  </React.Fragment>
                ),
              )}
            </SelectionCard.Group>
          )}
        />
      </ContentSection>
    </Container>
  );
};
